import { Box, Grid, Skeleton, Text, VStack } from '@chakra-ui/react';
import { useMemo } from 'react';

import { FormatType, LineChart, type LineData } from '@revelio/replots';

import type { CompanyMetrics } from '../hooks/useCompetitorComposition';
import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import { useCurrencyStore } from '../hooks/useCurrencyStore';
import { StatBox } from '../salary/StatBox';

interface GrowthProjectionCardProps {
  currentCompanyMetrics: CompanyMetrics | undefined;
  competitorMetrics: CompanyMetrics[];
  companyName: string | null;
  userSalary: number;
  isLoading: boolean;
}

interface Projection {
  current: number;
  marketBaseSalary: number;
  stayYear1: number;
  stayYear3: number;
  stayYear5: number;
  marketYear1: number;
  marketYear3: number;
  marketYear5: number;
  annualGrowthRate: number;
  marketAvgRate: number;
  vsMarket: number; // percentage point difference
}

const computeHistoricalGrowthRate = (
  salaryTs: Array<{ id: number; value: number | null }>
): number => {
  if (salaryTs.length < 12) return 0.03; // default 3% if insufficient data

  const validPoints = salaryTs.filter(
    (p) => p.value !== null && p.value > 0
  ) as Array<{ id: number; value: number }>;

  if (validPoints.length < 12) return 0.03;

  // Use last 12 months to calculate annualized rate
  const recent = validPoints.slice(-1)[0];
  const yearAgo = validPoints.slice(-13, -12)[0];

  if (!recent || !yearAgo || yearAgo.value === 0) return 0.03;

  return (recent.value - yearAgo.value) / yearAgo.value;
};

const project = (base: number, annualRate: number, years: number): number => {
  // Apply growth with diminishing returns over time
  // biome-ignore lint/style/useExponentiationOperator: migrated from develop
  const adjustedRate = annualRate * Math.pow(0.9, years - 1);
  // biome-ignore lint/style/useExponentiationOperator: migrated from develop
  return base * Math.pow(1 + adjustedRate, years);
};

export const GrowthProjectionCard = ({
  currentCompanyMetrics,
  competitorMetrics,
  companyName,
  userSalary,
  isLoading,
}: GrowthProjectionCardProps) => {
  const salaryFormatter = useCurrencyFormatter();
  const currencyCode = useCurrencyStore((s) => s.selected?.code ?? 'USD');

  const projection = useMemo((): Projection | null => {
    if (!currentCompanyMetrics || userSalary <= 0) return null;

    const companyRate = computeHistoricalGrowthRate(
      currentCompanyMetrics.salaryTimeseries
    );

    // Market average rate and salary across all competitors
    const peers = competitorMetrics.filter(
      (m) => m.companyId !== currentCompanyMetrics.companyId
    );
    const competitorRates = peers.map((m) =>
      computeHistoricalGrowthRate(m.salaryTimeseries)
    );
    const peerSalaries = peers
      .map((m) => m.salary)
      .filter((s): s is number => s !== null && s > 0);

    const marketAvgRate =
      competitorRates.length > 0
        ? competitorRates.reduce((sum, r) => sum + r, 0) /
          competitorRates.length
        : 0.03;

    // Market base salary is the average median salary across peer companies
    const marketBaseSalary =
      peerSalaries.length > 0
        ? peerSalaries.reduce((sum, s) => sum + s, 0) / peerSalaries.length
        : userSalary;

    const stayYear1 = project(userSalary, companyRate, 1);
    const stayYear3 = project(userSalary, companyRate, 3);
    const stayYear5 = project(userSalary, companyRate, 5);

    const marketYear1 = project(marketBaseSalary, marketAvgRate, 1);
    const marketYear3 = project(marketBaseSalary, marketAvgRate, 3);
    const marketYear5 = project(marketBaseSalary, marketAvgRate, 5);

    // Difference in percentage points (e.g., 5% vs 3% = +2pp)
    const vsMarket = (companyRate - marketAvgRate) * 100;

    return {
      current: userSalary,
      marketBaseSalary,
      stayYear1,
      stayYear3,
      stayYear5,
      marketYear1,
      marketYear3,
      marketYear5,
      annualGrowthRate: companyRate,
      marketAvgRate,
      vsMarket,
    };
  }, [currentCompanyMetrics, competitorMetrics, userSalary]);

  const chartData = useMemo((): LineData[] => {
    if (!projection) return [];

    const now = new Date();
    const makeDate = (yearsFromNow: number): string => {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() + yearsFromNow);
      return d.toISOString().slice(0, 7); // YYYY-MM format
    };

    return [
      {
        label: `Stay (${companyName ?? 'current'})`,
        values: [
          { date: makeDate(0), value: projection.current },
          { date: makeDate(1), value: projection.stayYear1 },
          { date: makeDate(3), value: projection.stayYear3 },
          { date: makeDate(5), value: projection.stayYear5 },
        ],
        color: '#3182CE', // blue for current
      },
      {
        label: 'Market Avg (peer companies)',
        values: [
          { date: makeDate(0), value: projection.marketBaseSalary },
          { date: makeDate(1), value: projection.marketYear1 },
          { date: makeDate(3), value: projection.marketYear3 },
          { date: makeDate(5), value: projection.marketYear5 },
        ],
        color: '#A0AEC0',
      },
    ];
  }, [projection, companyName]);

  if (isLoading) {
    return <Skeleton height="260px" borderRadius="md" />;
  }

  if (!projection) {
    return null;
  }

  const stayRate = (projection.annualGrowthRate * 100).toFixed(1);
  const marketRate = (projection.marketAvgRate * 100).toFixed(1);

  let vsColor = 'gray.600';
  let vsVerdict = 'On par with market';
  if (projection.vsMarket > 1) {
    vsColor = 'green.600';
    vsVerdict = 'Outpacing the market';
  } else if (projection.vsMarket < -1) {
    vsColor = 'red.500';
    vsVerdict = 'Below market growth';
  }

  return (
    <Box
      bg="gray.50"
      p={5}
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
    >
      <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
        Salary Growth: Stay vs Market
      </Text>
      <Text fontSize="xs" color="gray.500" mb={4}>
        Projected salary if you stay at {companyName ?? 'your current company'}{' '}
        ({stayRate}%/yr) vs moving to a peer company ({marketRate}%/yr avg)
      </Text>

      <Grid templateColumns="5fr 3fr" gap={4}>
        <Box height="200px">
          <LineChart
            data={chartData}
            format={FormatType.CURRENCY}
            currencyCode={currencyCode}
            colors={['#3182CE', '#A0AEC0']}
            showLegend
          />
        </Box>

        <VStack align="stretch" spacing={2}>
          <Grid templateColumns="1fr 1fr" gap={2}>
            <StatBox
              compact
              label="Your Salary"
              value={salaryFormatter(projection.current)}
              color="blue.600"
            />
            <StatBox
              compact
              label="Market Avg"
              value={salaryFormatter(projection.marketBaseSalary)}
              color="gray.500"
            />
          </Grid>
          <Grid templateColumns="1fr 1fr" gap={2}>
            <Box>
              <Text
                fontSize="2xs"
                color="blue.500"
                fontWeight="semibold"
                textAlign="center"
                mb={0.5}
              >
                Stay
              </Text>
              <StatBox
                compact
                label="1yr"
                value={salaryFormatter(projection.stayYear1)}
                color="blue.600"
              />
            </Box>
            <Box>
              <Text
                fontSize="2xs"
                color="gray.400"
                fontWeight="semibold"
                textAlign="center"
                mb={0.5}
              >
                Market
              </Text>
              <StatBox
                compact
                label="1yr"
                value={salaryFormatter(projection.marketYear1)}
                color="gray.500"
              />
            </Box>
          </Grid>
          <Grid templateColumns="1fr 1fr" gap={2}>
            <StatBox
              compact
              label="3yr Stay"
              value={salaryFormatter(projection.stayYear3)}
              color="blue.600"
            />
            <StatBox
              compact
              label="3yr Market"
              value={salaryFormatter(projection.marketYear3)}
              color="gray.500"
            />
          </Grid>
          <Box
            textAlign="center"
            pt={2}
            borderTop="1px solid"
            borderColor="gray.200"
          >
            <Text fontSize="sm" fontWeight="bold" color={vsColor}>
              {vsVerdict}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {projection.vsMarket > 0 ? '+' : ''}
              {projection.vsMarket.toFixed(1)}pp vs market avg
            </Text>
          </Box>
        </VStack>
      </Grid>

      <Text fontSize="xs" color="gray.400" mt={3}>
        Based on historical salary trends. Assumes no role change, promotion, or
        market disruption. Applies diminishing returns for longer projections.
      </Text>
    </Box>
  );
};
